import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import ko from "./ko.json";
import en from "./en.json";

/**
 * i18n foundation — ko default, en fallback.
 *
 * Display languages: **ko · en only**. The control web UI is fully localised in
 * Korean and English; every page/widget renders through these two resource
 * bundles (`ko.json` / `en.json`). Earlier builds shipped a six-language kiosk
 * set (ko/en/zh/vi/th/id) but the operator UI is now Korean/English exclusively.
 *
 * ⚠️ This is *independent* of the spoken-alarm broadcast languages. The alarm
 * TTS still speaks all six 협약 languages (see `alertPhrases.ts :: ALERT_LANGUAGES`
 * and the Settings broadcast card) — that set is deliberately NOT reduced here.
 *
 * Detection order: an explicit choice persisted in localStorage wins, then we
 * fall back to the browser/`<html lang>`. The chosen language is written back
 * to localStorage so the next visit (and the offline app shell) stays in the
 * same language. A legacy stored value outside ko/en (a user who had picked
 * zh/vi/th/id) is migrated away on boot so it cleanly resolves to ko/en.
 *
 * `react: { useSuspense: false }` keeps `useTranslation()` synchronous — these
 * resources are bundled, so there is nothing to suspend on, and disabling
 * suspense avoids accidental fallbacks during the first paint.
 */

export const SUPPORTED_LANGUAGES = ["ko", "en"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = "expertgas.lang.v3";

/**
 * Migrate a legacy persisted language (zh/vi/th/id, from the old 6-language
 * build) to a supported one. Runs before init so the detector never resolves to
 * a language we no longer bundle. Korean-family locales → ko, everything else →
 * removed so detection falls through to the browser/`<html lang>` (default ko).
 */
function migrateStoredLanguage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return;
    const base = stored.split("-")[0];
    if (base !== "ko" && base !== "en") {
      // Unsupported legacy pick — drop it so we fall back to ko/en cleanly.
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable (private mode) — ignore, detector handles it. */
  }
}

migrateStoredLanguage();

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ko: { translation: ko },
      en: { translation: en },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Don't downgrade "ko-KR" → fail; treat the base language as the match.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "htmlTag", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

/** Switch language and mirror onto <html lang> + localStorage. */
export function changeLanguage(lng: Language): void {
  void i18n.changeLanguage(lng);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
}

/** Effective base language right now ("ko" | "en"). */
export function currentLanguage(): Language {
  const base = (i18n.resolvedLanguage ?? i18n.language ?? "ko").split("-")[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as Language)
    : "ko";
}

// Keep <html lang> in sync whenever the language changes (e.g. via the toggle
// or the detector), so screen readers and the manifest see the right locale.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng.split("-")[0];
  }
});

export default i18n;
